import { combineReducers, configureStore, createListenerMiddleware, isRejected } from '@reduxjs/toolkit';
import { updateNotifications } from './slices/notificationSlice';
import { v4 as uuidv4 } from 'uuid';
import authReducer from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import instancesReducer from './slices/instancesSlice';
import imagesReducer from './slices/imagesSlice';
import flavorsReducer from './slices/flavorsSlice';
import overviewReducer from './slices/overviewSlice';
import securityGroupsReducer from './slices/securityGroupsSlice';
import securityRulesReducer from './slices/securityRulesSlice';
import privateNetworkReducer from './slices/privateNetworkSlice';
import volumesReducer from './slices/volumesSlice';
import notificationReducer from './slices/notificationSlice';
import keypairReducer from './slices/keypairsSlice';
import billingReducer from './slices/billingSlice';
import snapshotsReducer from './slices/snapshotsSlice';
import backupReducer from './slices/backupsSlice';
import loadBalancersReducer from './slices/loadBalancersSlice';
import clustersReducer from './slices/clustersSlice';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import regionsReducer from './slices/regionsSlice';
import billingHistoryReducer from './slices/billingHistorySlice'
import paymentReducer from './slices/paymentSlice';
import domainsReducer from './slices/domainsSlice';
import databasesReducer from './slices/databasesSlice';
import relationalDatabasesReducer from './slices/relationalDatabasesSlice';
import clickhouseDatabasesReducer from './slices/clickhouseDatabasesSlice';
import redisReducer from './slices/redisSlice';
import qdrantReducer from './slices/qdrantSlice';

const rootReducer = combineReducers({
    auth: authReducer,
    ui: uiReducer,
    instances: instancesReducer,
    images: imagesReducer,
    flavors: flavorsReducer,
    overview: overviewReducer,
    securityGroups: securityGroupsReducer,
    securityRules: securityRulesReducer,
    privateNetwork: privateNetworkReducer,
    volumes: volumesReducer,
    notifications: notificationReducer,
    keypairs: keypairReducer,
    billingForecast: billingReducer,
    snapshots: snapshotsReducer,
    backups: backupReducer,
    loadBalancers: loadBalancersReducer,
    clusters: clustersReducer,
    regions: regionsReducer,
    billingHistory: billingHistoryReducer,
    payment: paymentReducer,
    domains: domainsReducer,
    databases: databasesReducer,
    relationalDatabases: relationalDatabasesReducer,
    clickhouseDatabases: clickhouseDatabasesReducer,
    redis: redisReducer,
    qdrant: qdrantReducer,
});

const persistedReducer = persistReducer(
    {
        key: 'root',
        storage,
        whitelist: ['auth', 'ui'],
    },
    rootReducer
);

const fetchRejectedListener = createListenerMiddleware();
fetchRejectedListener.startListening({
    matcher: isRejected,
    effect: (action, { dispatch, getState }) => {
        // Only handle thunks that start with "fetch" (data-load operations)
        if (!action.type.startsWith('fetch') && !action.type.includes('/fetch')) return;

        // Don't double-notify if the socket already sent an error for this
        const state = getState() as any;
        const existing = state.notifications?.data ?? [];
        if (existing.some((n: any) => n.type === 'error' && n.autoDismiss === false)) return;

        const id = uuidv4();
        dispatch(updateNotifications({
            id,
            message: 'Failed to load data. Please refresh the page.',
            type: 'error',
            autoDismiss: true,
        }));
        setTimeout(() => {
            dispatch({ type: 'notifications/removeNotification', payload: { id } });
        }, 8000);
    },
});

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }).prepend(fetchRejectedListener.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
